import { formatInTimeZone } from 'date-fns-tz';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

import { isActiveFilterRow } from './isActiveFilterRow';
import { FilterColumnDef, FilterRow } from './types';

type LegRun = ShipmentOrderLegRunForPlanEntity;
type ColumnsByKey = Map<string, FilterColumnDef>;

/**
 * ISO datetime から 'HH:mm' を取り出す。解釈できなければ null。
 * Date#getHours/getMinutes は端末のタイムゾーン依存になり、TZ=UTC 等の端末では
 * 同じ ISO 値でも異なる時刻に見えてしまう。本アプリの表示規約に合わせ Asia/Tokyo で
 * 固定フォーマットする。
 *
 * date-fns-tz の `format(date, fmt, { timeZone })` は z/x/O 等のオフセット表記
 * トークンにしか timeZone を適用せず、HH/mm 等の通常トークンは実行環境の
 * ローカルタイムゾーンのまま date-fns の format に委譲される（実機検証で確認）。
 * そのため src/decorators/datetime.decorator.ts の toHourMinutes と同じ書き方は
 * 使わず、実際に指定タイムゾーンへ変換する formatInTimeZone を使う。
 */
export function toHHmm(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return formatInTimeZone(d, 'Asia/Tokyo', 'HH:mm');
}

function matchText(raw: string | string[] | null | undefined, operator: string, needle: string): boolean {
  const list = Array.isArray(raw) ? raw : [raw];
  const values = list.filter((v): v is string => v != null && v !== '');

  if (operator === 'empty') return values.length === 0;
  if (operator === 'notEmpty') return values.length > 0;
  // 未設定データは空判定以外ではマッチさせない。
  if (values.length === 0) return false;

  const n = needle.trim().toLowerCase();
  const lower = values.map((v) => v.toLowerCase());
  switch (operator) {
    case 'contains': return lower.some((v) => v.includes(n));
    // 否定は「肯定条件に一致する要素が 1 つも無い」。some(否定条件) だと
    // ['冷凍肉','野菜'] が「冷凍を含まない」に一致してしまう。
    case 'notContains': return !lower.some((v) => v.includes(n));
    case 'equals': return lower.some((v) => v === n);
    case 'notEquals': return !lower.some((v) => v === n);
    default: return false;
  }
}

function matchRow(legRun: LegRun, row: FilterRow, column: FilterColumnDef): boolean {
  switch (column.type) {
    case 'text': {
      if (row.value.kind !== 'text' && row.value.kind !== 'none') return false;
      const needle = row.value.kind === 'text' ? row.value.value : '';
      return matchText(column.getValue(legRun), row.operator, needle);
    }
    case 'number': {
      const v = column.getValue(legRun);
      if (v == null) return false;
      if (row.value.kind === 'numberRange') {
        const { min, max } = row.value;
        if (min == null || max == null) return false;
        return v >= min && v <= max;
      }
      if (row.value.kind !== 'number' || row.value.value == null) return false;
      const n = row.value.value;
      switch (row.operator) {
        case 'gte': return v >= n;
        case 'lte': return v <= n;
        case 'eq': return v === n;
        case 'neq': return v !== n;
        default: return false;
      }
    }
    case 'time': {
      if (row.value.kind !== 'time') return false;
      const v = toHHmm(column.getValue(legRun));
      if (v == null) return false;
      switch (row.operator) {
        case 'timeGte': return v >= row.value.value;
        case 'timeLte': return v <= row.value.value;
        case 'timeEq': return v === row.value.value;
        default: return false;
      }
    }
    case 'enumSelect': {
      if (row.value.kind !== 'enum') return false;
      const values = column.getValue(legRun) ?? [];
      if (values.length === 0) return false;
      const selected = new Set(row.value.values);
      const intersects = values.some((v) => selected.has(v));
      return row.operator === 'anyOf' ? intersects : !intersects;
    }
    case 'idSelect': {
      if (row.value.kind !== 'id') return false;
      const values = column.getValue(legRun) ?? [];
      if (values.length === 0) return false;
      const selected = new Set(row.value.values);
      const intersects = values.some((v) => selected.has(v));
      return row.operator === 'anyOf' ? intersects : !intersects;
    }
    case 'boolean': {
      if (row.value.kind !== 'boolean' || row.value.value == null) return false;
      const v = column.getValue(legRun);
      if (v == null) return false;
      return v === row.value.value;
    }
    default:
      return false;
  }
}

/**
 * 有効な行だけを取り出し、連続する AND を 1 グループにまとめる。
 * 無効な行は除外され、空になったグループは OR の項ごと消える。
 */
function activeGroups(rows: FilterRow[], columnsByKey: ColumnsByKey): { row: FilterRow; column: FilterColumnDef }[][] {
  const groups: { row: FilterRow; column: FilterColumnDef }[][] = [];
  let current: { row: FilterRow; column: FilterColumnDef }[] = [];

  rows.forEach((row) => {
    if (row.combinator === 'or') {
      if (current.length > 0) groups.push(current);
      current = [];
    }
    const column = columnsByKey.get(row.column);
    if (column && isActiveFilterRow(row, column)) current.push({ row, column });
  });
  if (current.length > 0) groups.push(current);

  return groups;
}

/** 絞り込みに寄与する行が 1 つでもあるか（件数バッジと「フィルタ適用中」の判定に使う）。 */
export function hasActiveFilter(rows: FilterRow[], columnsByKey: ColumnsByKey): boolean {
  return activeGroups(rows, columnsByKey).length > 0;
}

export function evaluateFilters(legRun: LegRun, rows: FilterRow[], columnsByKey: ColumnsByKey): boolean {
  const groups = activeGroups(rows, columnsByKey);
  if (groups.length === 0) return true;
  return groups.some((group) => group.every(({ row, column }) => matchRow(legRun, row, column)));
}
